import {useState} from "react";
import {KatButton, KatModal} from "@amzn/katal-react";
import * as React from "react";
import {LighthouseApi} from "src/app/api/LighthouseApi";
import {ReviewStatus} from "src/app/constant/Enums";
import {isSuccessResult} from "src/app/constant/Constants";
import {MAX_COMMENT_LENGTH} from "src/app/api/model/request/AddCommentRequest";

interface ResumeCaseDialogProps {
    visible: boolean;
    reviewId: string | null;
    onClose: () => void;
    onSuccess: () => void;
}

/**
 * Resume a paused case. Captures a mandatory reason (recorded in the case history) and calls
 * UpdateReviewStatus with targetStatus=PENDING. The DDB Streams pipeline recreates the Mission
 * Control task; on success the parent refreshes the current page via onSuccess.
 *
 * Mirrors BulkActionDialog so the API call lives in the dialog, not the page's action dispatcher.
 */
const ResumeCaseDialog = (props: ResumeCaseDialogProps) => {
    const {visible, reviewId, onClose, onSuccess} = props;
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const reset = () => {
        setReason('');
        setSubmitting(false);
        setErrorMsg('');
    };

    const handleClose = () => {
        if (submitting) return;   // block closing while the API call is in flight
        reset();
        onClose();
    };

    const handleConfirm = async () => {
        const notes = reason.trim();
        if (!notes || !reviewId) return;
        setErrorMsg('');
        setSubmitting(true);
        try {
            const response = await LighthouseApi.updateReviewStatus({
                reviewId,
                targetStatus: ReviewStatus.PENDING,
                notes
            });
            if (isSuccessResult(response.result)) {
                reset();
                onSuccess();
            } else {
                setErrorMsg(response.msg || 'Failed to resume case');
                setSubmitting(false);
            }
        } catch (error: any) {
            setErrorMsg(error.message || 'Unexpected error occurred');
            setSubmitting(false);
        }
    };

    return (
        <KatModal visible={visible} data-testid="resume-case-modal" onClose={handleClose}>
            <span slot="title">Resume Case</span>
            <div>
                <p>Add a reason for resuming this case.</p>
                <textarea
                    className="lighthouse-remarks-textarea"
                    value={reason}
                    maxLength={MAX_COMMENT_LENGTH}
                    disabled={submitting}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason for resuming..."
                    data-testid="resume-reason"
                />
                {errorMsg && (
                    <p style={{color: '#d13212'}} data-testid="resume-error">{errorMsg}</p>
                )}
            </div>
            <div slot="footer" className="lighthouse-modal-footer">
                <KatButton label="Cancel" variant="link" onClick={handleClose} data-testid="resume-cancel" />
                <KatButton
                    label="Confirm Resume"
                    onClick={handleConfirm}
                    disabled={submitting || reason.trim().length === 0}
                    data-testid="resume-confirm"
                />
            </div>
        </KatModal>
    );
};

export default ResumeCaseDialog;
